import { z } from "zod";

/**
 * Message shown to users when data fails schema validation. A ZodError describes
 * the mismatch for developers ("Expected string, received null at ...") and has
 * nothing suitable for display, so we substitute a fixed, friendly message and
 * keep the original ZodError as the `cause` for diagnostics.
 */
const FRIENDLY_MESSAGE = "Sorry, something went wrong.";

const userSafeError = (cause: z.ZodError): Error =>
  new Error(FRIENDLY_MESSAGE, { cause });

/**
 * Swaps a ZodError for an Error carrying a user-safe message. Anything that is
 * not a ZodError passes through unchanged, so this is safe to drop into any
 * catch or promise-rejection handler.
 */
export function toUserSafeError(error: z.ZodError): Error;
export function toUserSafeError<E>(error: E): E;
export function toUserSafeError(error: unknown): unknown {
  return error instanceof z.ZodError ? userSafeError(error) : error;
}

/**
 * Parses `data` against `schema`, like `schema.parse(data)`, but throws an Error
 * with a user-safe message on failure instead of a raw ZodError. Use at manual
 * validation sites (e.g. parsing an API response by hand) so failures surface
 * the same friendly message as automatically-validated HeyApi calls.
 */
export const parseSafely = <T>(schema: z.ZodType<T>, data: unknown): T => {
  const result = schema.safeParse(data);
  if (!result.success) throw userSafeError(result.error);
  return result.data;
};
